import { mergeAttributes } from '@tiptap/core';
import Document from '@tiptap/extension-document';
import Text from '@tiptap/extension-text';
import BulletListBase from '@tiptap/extension-bullet-list';
import HeadingBase from '@tiptap/extension-heading';
import ListItem from '@tiptap/extension-list-item';
import OrderedListBase from '@tiptap/extension-ordered-list';
import ParagraphBase from '@tiptap/extension-paragraph';
import BoldBase from '@tiptap/extension-bold';
import ItalicBase from '@tiptap/extension-italic';
import LinkBase from '@tiptap/extension-link';
import { Placeholder, UndoRedo } from '@tiptap/extensions';
import { HighlightSuggestion as HighlightSuggestionBase } from './highlightSuggestion';
import { NudgeSuggestion as NudgeSuggestionBase, addNudgeIdAttribute } from './nudgeSuggestion';
import { OneLiner } from './oneLiner';
import { ParagraphReplacement } from './paragraphReplacement';

const Paragraph = addNudgeIdAttribute(ParagraphBase);
const BulletList = addNudgeIdAttribute(BulletListBase);
const OrderedList = addNudgeIdAttribute(OrderedListBase);

const Heading = ({ disabledNodes }) => addNudgeIdAttribute(HeadingBase).configure({
  levels: [
    disabledNodes.h1 ? null : 1, disabledNodes.h2 ? null : 2, disabledNodes.h3 ? null : 3,
  ].filter(Boolean),
});

const Italic = ItalicBase.extend({
  addKeyboardShortcuts() {
    return {
      'Mod-i': () => (this.editor.isActive('heading') ? false : this.editor.commands.toggleItalic()),
      'Mod-I': () => (this.editor.isActive('heading') ? false : this.editor.commands.toggleItalic()),
    };
  },
});

const Bold = BoldBase.extend({
  addKeyboardShortcuts() {
    return {
      'Mod-b': () => (this.editor.isActive('heading') ? false : this.editor.commands.toggleBold()),
      'Mod-B': () => (this.editor.isActive('heading') ? false : this.editor.commands.toggleBold()),
    };
  },
});

const Link = LinkBase
  .extend({
    addKeyboardShortcuts: () => ({
      'Mod-k': ({ editor }) => {
        if (editor.isActive('heading')) return false;

        editor.emit('linkEdit');
        return true;
      },
    }),
    renderHTML({ HTMLAttributes }) {
      // workaround https://github.com/ueberdosis/tiptap/issues/4929
      // [Bug]: generateJSON can return links href as number #4929
      //
      // force HTMLAttributes.href to be a string
      return ['a', mergeAttributes(this.options.HTMLAttributes, {
        ...HTMLAttributes, href: String(HTMLAttributes.href),
      }), 0];
    },
  })
  .configure({ openOnClick: false });

const configuredPlaceholder = ({ getTiptapWrap }) => Placeholder.configure({
  includeChildren: true,
  showOnlyCurrent: false,
  placeholder: ({ node }) => {
    if (getTiptapWrap().placeholder) {
      return getTiptapWrap().placeholder;
    }
    if (node.type.name === 'heading') {
      return 'Add header text';
    }
    return 'Add text';
  },
});

const HighlightSuggestion = ({ getTiptapWrap }) => HighlightSuggestionBase.configure({
  onMouseOver: (event) => getTiptapWrap().onHighlightMouseOver(event),
  onMouseOut: (event) => getTiptapWrap().onHighlightMouseOut(event),
});

const NudgeSuggestion = ({ getTiptapWrap }) => NudgeSuggestionBase.configure({
  onMouseOver: (event) => getTiptapWrap().onNudgeMouseOver(event),
  onMouseOut: (event) => getTiptapWrap().onNudgeMouseOut(event),
});

export const extensions = ({ getTiptapWrap, disabledNodes }) => {
  const isDisabled = (key) => disabledNodes[key];

  return [
    // base
    Document,
    Text,

    // top-level nodes
    isDisabled('paragraph') ? ParagraphReplacement : Paragraph,
    isDisabled('heading') ? null : Heading({ disabledNodes }),
    isDisabled('list') ? null : BulletList,
    isDisabled('list') ? null : ListItem,
    isDisabled('list') || isDisabled('orderedList') ? null : OrderedList,

    // marks
    isDisabled('bold') ? null : Bold,
    NudgeSuggestion({ getTiptapWrap }),
    HighlightSuggestion({ getTiptapWrap }),
    isDisabled('italic') ? null : Italic,
    isDisabled('link') ? null : Link,
    // italic controlled by disabledNodes.italic

    // functionality
    UndoRedo,
    configuredPlaceholder({ getTiptapWrap }),
  ].filter(Boolean);
};

export const inlineEditorExtensions = ({ getTiptapWrap }) => [
  // base
  OneLiner,
  Text,

  // top-level nodes
  Paragraph,

  // marks
  NudgeSuggestion({ getTiptapWrap }),
  HighlightSuggestion({ getTiptapWrap }),

  // functionality
  UndoRedo,
  configuredPlaceholder({ getTiptapWrap }),
];
